import HardBreak from "@tiptap/extension-hard-break";
import Link from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
} from "lucide-react";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

export function TextareaEditor({
  className,
  name,
  onChange,
  value = "",
}: {
  className?: string;
  name: string;
  onChange: (html: string) => void;
  value: string;
}) {
  const editor = useEditor({
    content: value,
    editorProps: {
      attributes: {
        class:
          "w-full min-h-[100px] max-h-[200px] p-3 outline-none overflow-auto text-gray-800 bg-white rounded-md",
      },
      handleKeyDown(view, event) {
        if (event.key === "Enter" && event.shiftKey === false) {
          const { state } = view;
          const { $from } = state.selection;

          if ($from.parent.content.size === 0) {
            event.preventDefault();
            editor?.chain().focus().setHardBreak().run();

            return true;
          }
        }

        return false;
      },
    },
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      HardBreak.configure({
        keepMarks: true,
      }),
      Underline,
      Link.configure({
        HTMLAttributes: {
          class: "text-blue-500 underline cursor-pointer",
        },
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: "Your message...",
      }),
    ],
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      if (html !== value) {
        onChange?.(html);
      }
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const currentContent = editor.getHTML();

    if (value !== currentContent) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  useEffect(
    () => () => {
      if (editor && !editor.isDestroyed) {
        editor.destroy();
      }
    },
    [editor],
  );

  if (!editor) {
    return null;
  }

  return (
    <div className={twMerge("w-[260px]", className)} data-name={name}>
      <BubbleMenu
        className="flex items-center gap-1 bg-white rounded-md shadow-lg p-1 border border-gray-200"
        editor={editor}
        tippyOptions={{ duration: 100 }}
      >
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("bold") ? "bg-gray-100" : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
          type="button"
        >
          <Bold className="text-gray-600" size={16} />
        </button>
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("italic") ? "bg-gray-100" : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
          type="button"
        >
          <Italic className="text-gray-600" size={16} />
        </button>
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("underline") ? "bg-gray-100" : ""}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
          type="button"
        >
          <UnderlineIcon className="text-gray-600" size={16} />
        </button>
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("bulletList") ? "bg-gray-100" : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
          type="button"
        >
          <List className="text-gray-600" size={16} />
        </button>
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("orderedList") ? "bg-gray-100" : ""}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
          type="button"
        >
          <ListOrdered className="text-gray-600" size={16} />
        </button>
        <button
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive("link") ? "bg-gray-100" : ""}`}
          onClick={() => {
            const url = window.prompt(
              "Enter URL",
              editor.getAttributes("link").href || "https://",
            );

            if (url === null) return;
            if (url === "") {
              editor.chain().focus().extendMarkRange("link").unsetLink().run();
            } else {
              editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: url })
                .run();
            }
          }}
          title="Link"
          type="button"
        >
          <LinkIcon className="text-gray-600" size={16} />
        </button>
      </BubbleMenu>

      <EditorContent editor={editor} />
    </div>
  );
}
